import React, { useEffect, useRef } from "react";
import "./TextArea.scss";
import { Button } from "@/utils/ui/Button/Button";

interface Props {
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur: any;
  placeholder?: string;
  error?: string;
  touched?: boolean;
  label: string;
  showRequired: boolean;
  className?: string; // Optional className prop for custom styles
  style?: React.CSSProperties; // Optional inline style prop
  characterLimit?: number; // Optional character limit prop for input validation and character counter
  disabled?: boolean;
  rightElement?: React.ReactNode;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  readOnly?: boolean;
  rows?: number;
  autoExpandRows?: boolean;
  onSend?: () => void;
  isSending?: boolean;
  labelCheckbox?: boolean;
  labelCheckboxChecked?: boolean;
  onLabelCheckboxChange?: (checked: boolean) => void;
  disableLabelCheckbox?: boolean;
  readOnlyMode?: boolean;
  hideLabel?: boolean;
  // Stretching the textarea's wrapper to the full height of a flex-column
  // parent (the default) is needed so an absolutely-positioned onSend button
  // anchors to the parent's bottom, not just the textarea's own. In a plain
  // flex-column form field, though, that same stretch pushes the touched/
  // error message (rendered as this component's own sibling right after the
  // wrapper) down to wherever the parent's height ends, instead of right
  // under the textarea - pass false there.
  fullHeight?: boolean;
}

export const TextArea: React.FC<Props> = ({
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  touched,
  showRequired,
  label,
  characterLimit,
  disabled = false,
  className = "p-0",
  rightElement,
  onKeyDown,
  readOnly = false,
  rows = 4,
  autoExpandRows = false,
  style,
  onSend,
  isSending = false,
  labelCheckbox = false,
  labelCheckboxChecked,
  onLabelCheckboxChange,
  disableLabelCheckbox,
  readOnlyMode = false,
  hideLabel = false,
  fullHeight = true,
  ...props
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (autoExpandRows && textareaRef.current) {
      const textarea = textareaRef.current;
      const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight);
      const maxHeight = style?.maxHeight
        ? parseInt(style.maxHeight as string)
        : null;
      const contentRows = Math.ceil(textarea.scrollHeight / lineHeight);
      if (maxHeight) {
        const maxRowsByHeight = Math.floor(maxHeight / lineHeight);
        textarea.rows = Math.min(contentRows, maxRowsByHeight, 100);
      } else {
        textarea.rows = Math.min(contentRows, 100);
      }
    }
  }, [value, style?.maxHeight, autoExpandRows]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && onSend) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  return (
    <>
      {!hideLabel && (
        <div
          className={`${className} d-flex mb-1 gap-2 align-items-center justify-content-between w-100`}
        >
          <div className="d-flex w-100 gap-2">
            {labelCheckbox && !readOnlyMode && (
              <input
                type="checkbox"
                checked={labelCheckboxChecked}
                onChange={(e) => onLabelCheckboxChange?.(e.target.checked)}
                className="me-2"
                style={{ cursor: "pointer" }}
                disabled={disableLabelCheckbox}
              />
            )}
            <label className="form-label">
              {label}
              {showRequired && <span className="required ms-1">*</span>}
            </label>
            {characterLimit && (
              <small
                className={
                  value.trim().length > characterLimit
                    ? "text-danger"
                    : "text-muted"
                }
              >
                ( {value.trim().length}/{characterLimit})
              </small>
            )}
          </div>
          {rightElement && <>{rightElement}</>}
        </div>
      )}
      <div
        className="position-relative"
        style={{ width: "100%", ...(fullHeight ? { height: "100%" } : {}) }}
      >
        <textarea
          ref={textareaRef}
          className={`form-control textarea-scroll ${
            error && touched ? "is-invalid" : ""
          }`}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          disabled={disabled || isSending}
          rows={!autoExpandRows ? 4 : rows}
          style={{
            ...style,
            paddingRight: onSend ? "48px" : undefined,
            width: "100%",
          }}
          onKeyDown={handleKeyDown}
          readOnly={readOnly}
          {...props}
        />
        {onSend && (
          <Button
            type="primary"
            className="position-absolute border-0 end-0 bottom-0 mb-4 me-2 rounded-circle"
            style={{
              zIndex: 1,
              backgroundColor: "#0d6efd",
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onSend && value.trim() && !disabled && !isSending) {
                onSend();
              }
            }}
            isDisabled={!value.trim() || disabled || isSending}
          >
            {isSending ? (
              <div
                className="spinner-border spinner-border-sm text-white"
                role="status"
              >
                <span className="visually-hidden">Sending...</span>
              </div>
            ) : (
              <i
                className="bi bi-send text-white"
                style={{ fontSize: "16px" }}
              />
            )}
          </Button>
        )}
      </div>
      {touched && error && <div className="form-text text-danger">{error}</div>}
    </>
  );
};

export default TextArea;
